import { getToken, clearToken } from './auth/auth';
import { API_BASE_URL } from './config/runtime';

const API_BASE = API_BASE_URL;

function getHeaders() {
    const token = getToken();
    if (!token) return null;
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
}

export async function getMyStats() {
    const headers = getHeaders();
    if (!headers) return null;
    try {
        const res = await fetch(`${API_BASE}/me/stats`, { headers });
        if (!res.ok) throw new Error('Failed to fetch stats');
        return await res.json();
    } catch (err) {
        console.warn(err);
        return null;
    }
}

async function saveResult(endpoint, payload) {
    const headers = getHeaders();
    if (!headers) {
        saveAsGuest(endpoint, payload);
        try {
            window.dispatchEvent(new CustomEvent('results-updated', { detail: { endpoint, mode: 'guest' } }));
        } catch { }
        return;
    }
    try {
        const res = await fetch(`${API_BASE}${endpoint}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        });
        if (res.status === 401 || res.status === 403) {
            console.warn(`[Auth] Session invalid (Status ${res.status}). Clearing token and falling back to Guest Mode.`);
            clearToken();
            saveAsGuest(endpoint, payload);
            // Ensure MyPage updates immediately even when we fall back to guest-mode queue.
            try {
                window.dispatchEvent(new CustomEvent('results-updated', { detail: { endpoint, mode: 'guest' } }));
            } catch { }
            return;
        }
        if (!res.ok) throw new Error(`Failed to save to ${endpoint} (Status ${res.status})`);
        try {
            window.dispatchEvent(new CustomEvent('results-updated', { detail: { endpoint, mode: 'server' } }));
        } catch { }
    } catch (err) {
        console.warn(`[Error] Failed to save result to ${endpoint}:`, err);
        saveAsGuest(endpoint, payload);
        try {
            window.dispatchEvent(new CustomEvent('results-updated', { detail: { endpoint, mode: 'guest' } }));
        } catch { }
    }
}

function saveAsGuest(endpoint, payload) {
    console.log(`[Guest] Saving pending result for ${endpoint}`);
    if (!payload.id) {
        payload.id = 'guest_' + Date.now();
    }
    const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
    const exists = pending.some(p => p.endpoint === endpoint && JSON.stringify(p.payload) === JSON.stringify(payload));
    if (!exists) {
        pending.push({ endpoint, payload, timestamp: Date.now() });
        localStorage.setItem('pendingResults', JSON.stringify(pending));
    }
}

export async function syncPendingResults() {
    const headers = getHeaders();
    if (!headers) return;
    const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
    if (pending.length === 0) return;
    console.log(`Syncing ${pending.length} pending results...`);
    const newPending = [];
    for (const item of pending) {
        try {
            const res = await fetch(`${API_BASE}${item.endpoint}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(item.payload)
            });
            if (!res.ok) {
                console.error(`Failed to sync result to ${item.endpoint}`, await res.text());
                newPending.push(item);
            }
        } catch (err) {
            console.error(`Error syncing result to ${item.endpoint}`, err);
            newPending.push(item);
        }
    }
    if (newPending.length > 0) {
        localStorage.setItem('pendingResults', JSON.stringify(newPending));
    } else {
        localStorage.removeItem('pendingResults');
    }
}

export function saveLoveResult(payload) { return saveResult('/me/love-test', payload); }
export function saveCharacterResult(payload) { return saveResult('/me/character-test', payload); }
export function saveBeyondResult(payload) { return saveResult('/me/results', { ...payload, test_type: 'beyond' }); }
export function saveChugumiResult(payload) { return saveResult('/me/chugumi-test', payload); }
export function savePersonalityResult(payload) { return saveResult('/me/seb-test', payload); }
export function saveMegaResult(payload) { return saveResult('/me/results', payload); }

export async function getResultDetail(id) {
    const headers = getHeaders();
    if (!headers) return null;
    try {
        const res = await fetch(`${API_BASE}/me/results/${id}`, { headers });
        if (!res.ok) throw new Error('Failed to fetch result detail');
        return await res.json();
    } catch (err) {
        console.warn(err);
        return null;
    }
}

export async function compareHarmony(id1, id2) {
    try {
        const res = await fetch(`${API_BASE}/compat/compare?id1=${encodeURIComponent(id1)}&id2=${encodeURIComponent(id2)}`);
        if (!res.ok) {
            const errData = await res.json();
            throw new Error(errData.detail || 'Failed to fetch harmony comparison');
        }
        return await res.json();
    } catch (err) {
        console.error('Harmony Error:', err);
        throw err;
    }
}

// ── Collaborative Harmony System ──────────────────────────────────────────────

export async function searchUsers(query) {
    const res = await fetch(`${API_BASE}/compat/search?query=${encodeURIComponent(query)}`);
    if (!res.ok) throw new Error('User search failed');
    return await res.json();
}

export async function sendHarmonyRequest(toUsername) {
    const headers = getHeaders();
    if (!headers) throw new Error('로그인이 필요합니다.');
    const res = await fetch(`${API_BASE}/compat/requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ to_username: toUsername })
    });
    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Request failed');
    }
    return await res.json();
}

export async function getHarmonyRequests() {
    const headers = getHeaders();
    if (!headers) return { received: [], sent: [] };
    const res = await fetch(`${API_BASE}/compat/requests`, { headers, cache: 'no-store' });
    if (!res.ok) return { received: [], sent: [] };
    return await res.json();
}

export async function acceptHarmonyRequest(requestId) {
    const headers = getHeaders();
    const res = await fetch(`${API_BASE}/compat/requests/${requestId}/accept`, { method: 'POST', headers });
    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Accept failed');
    }
    return await res.json();
}

export async function rejectHarmonyRequest(requestId) {
    const headers = getHeaders();
    const res = await fetch(`${API_BASE}/compat/requests/${requestId}/reject`, { method: 'POST', headers });
    if (!res.ok) throw new Error('Reject failed');
    return await res.json();
}

export async function cancelHarmonyRequest(requestId) {
    const headers = getHeaders();
    const res = await fetch(`${API_BASE}/compat/requests/${requestId}/cancel`, { method: 'POST', headers });
    if (!res.ok) throw new Error('Cancel failed');
    return await res.json();
}

export async function listMyHarmonies() {
    const headers = getHeaders();
    if (!headers) return [];
    const res = await fetch(`${API_BASE}/compat`, { headers, cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return data.harmonies || [];
}

export async function getCollaborativeHarmonyDetail(id) {
    const headers = getHeaders();
    const res = await fetch(`${API_BASE}/compat/${id}`, { headers, cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to fetch harmony detail');
    return await res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// getInnerUnifiedReport
//
// 변경 내용 (이전 버전 대비):
//   1. axisContext (S/E/F 유형 심리구조 전체) → userPrompt에 주입
//   2. beyondContext (이상향 desc 전체)       → userPrompt에 주입
//   3. premiumContext (성격유형 psychology/love) → userPrompt에 주입
//   4. 점수 괄호 삽입 패턴 방지 지침 포함
//   5. BEYOND_CODE_TO_SCORE를 logic.py와 동일하게 { A:30, B:65, C:85 } 로 맞춤
//   6. backend에는 { userPrompt, mode } 만 전송 — logic.py 수정 불필요
// ─────────────────────────────────────────────────────────────────────────────

// logic.py의 BEYOND_CODE_TO_SCORE와 동일
const BEYOND_SCORE_MAP = { A: 30, B: 65, C: 85 };

const fmtGap = (n) => (n >= 0 ? `+${n}` : `${n}`);

const fmtAxis = (label, d) => {
    if (!d?.title) return '';
    const lines = [
        `## ${label}: [${d.title}]`,
        `요약: ${d.summary || ''}`,
        '',
        `심리 구조:`,
        d.analysis || '',
        '',
    ];
    if (d.innerMonologue?.length) {
        lines.push('이 사람이 속으로 하는 말:');
        d.innerMonologue.forEach((m) => lines.push(`  · ${m}`));
        lines.push('');
    }
    if (d.relationshipLoop) {
        lines.push(`반복 루프: ${d.relationshipLoop}`);
        lines.push('');
    }
    if (d.triggerMap?.length) {
        lines.push('이 반응이 촉발되는 실제 상황:');
        d.triggerMap.forEach((t) => lines.push(`  · ${t}`));
        lines.push('');
    }
    if (d.whatPartnerNeeds?.length) {
        lines.push('상대방이 실제로 필요로 하는 것:');
        d.whatPartnerNeeds.forEach((n) => lines.push(`  · ${n}`));
    }
    return lines.join('\n');
};

const fmtBeyond = (label, d) => {
    if (!d?.title) return '';
    return [`## ${label}: [${d.title}]`, d.desc || ''].join('\n');
};

const fmtPremium = (d) => {
    if (!d?.title) return '';
    return [
        `## 현재 성격 유형: ${d.title}`,
        `심리적 특성: ${d.psychology || ''}`,
        `연애에서의 작동 방식: ${d.love || ''}`,
    ].join('\n');
};

export const getInnerUnifiedReport = async ({
    mega,
    beyond,
    mode,
    axisContext,
    beyondContext,
    premiumContext,
}) => {
    const headers = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // ── 점수 추출 ──────────────────────────────────────────────────────────
    const megaPayload = mega?.payload || mega;
    const megaScoresRaw =
        megaPayload?.seb?.scores ||
        megaPayload?.scores ||
        megaPayload?.summary?.score_seb ||
        { S: 50, E: 50, F: 50 };

    const scores = {
        S: Number(megaScoresRaw.S ?? 50),
        E: Number(megaScoresRaw.E ?? 50),
        F: Number(megaScoresRaw.F ?? megaScoresRaw.B ?? 50),
    };

    // ── Beyond 코드 추출 ───────────────────────────────────────────────────
    const beyondPayload = beyond?.payload || beyond;
    const bS = beyondPayload?.S ?? beyondPayload?.summary?.S ?? 'A';
    const bE = beyondPayload?.E ?? beyondPayload?.summary?.E ?? 'A';
    const bB = beyondPayload?.B ?? beyondPayload?.summary?.B ?? 'A';

    const idealS = BEYOND_SCORE_MAP[bS] ?? 50;
    const idealE = BEYOND_SCORE_MAP[bE] ?? 50;
    const idealF = BEYOND_SCORE_MAP[bB] ?? 50;

    const gapS = idealS - scores.S;
    const gapE = idealE - scores.E;
    const gapF = idealF - scores.F;

    const coreAxis = [
        ['S', Math.abs(gapS)],
        ['E', Math.abs(gapE)],
        ['F', Math.abs(gapF)],
    ].sort((a, b) => b[1] - a[1])[0][0];

    const axisK = coreAxis === 'S' ? '감정 타이밍' : coreAxis === 'E' ? '관계 거리' : '행동 기준';
    const axisGap = coreAxis === 'S' ? Math.abs(gapS) : coreAxis === 'E' ? Math.abs(gapE) : Math.abs(gapF);

    const fallbackReport = {
        section1: `현재 반응 패턴의 중심은 ${axisK} 축입니다. 이 축에서 현실과 이상 간 간격이 커서 관계 해석이 흔들릴 수 있습니다.`,
        section2: '당신은 관계에서 안정성과 예측 가능성을 원하면서도, 동시에 이해받고 있다는 확신을 필요로 합니다.',
        section3: '원하는 관계 수준은 분명하지만, 실제 대화 타이밍과 표현 방식이 그 기대를 충분히 따라가지 못하는 구간이 보입니다.',
        section4: '갈등이 발생하면 즉시 해결하려는 쪽과 시간을 두려는 쪽의 리듬 차이로 같은 오해가 반복되기 쉽습니다.',
        section5: `핵심은 성격 문제가 아니라 운영 방식 문제입니다. 특히 ${axisK} 축(격차 ${axisGap})을 먼저 맞추면 체감 갈등이 빠르게 줄어듭니다.`,
        section6: 'S/E/B(행동) 축을 동시에 바꾸려 하지 말고, 가장 큰 축 1개부터 규칙을 정하면 개선 속도가 올라갑니다.',
        section7: '바로 적용: 1) 상태 공유 한 줄 2) 재대화 시간 고정 3) 비난 대신 요청형 문장.',
        section8: '한 줄 결론: 맞지 않는 사람이 아니라, 맞추는 방법을 아직 정하지 않은 상태입니다.',
        _fallback: true,
    };

    // ── 각 블록 직렬화 ─────────────────────────────────────────────────────
    const axisBlock = [
        fmtAxis('S축 감정 민감도 현재 작동 방식', axisContext?.S),
        fmtAxis('E축 관계 거리 현재 작동 방식', axisContext?.E),
        fmtAxis('F축 행동 추진력 현재 작동 방식', axisContext?.F),
    ].filter(Boolean).join('\n\n---\n\n');

    const beyondBlock = [
        fmtBeyond('S축 정서 스타일 이상향', beyondContext?.S),
        fmtBeyond('E축 거리감 이상향', beyondContext?.E),
        fmtBeyond('B축 연애 신념 이상향', beyondContext?.B),
    ].filter(Boolean).join('\n\n');

    const premiumBlock = fmtPremium(premiumContext);

    // ── userPrompt 조립 ────────────────────────────────────────────────────
    const userPrompt = `
# 분석 수치

- 감정 민감도(S): 현재 ${scores.S}점 → 이상 ${idealS}점 (격차 ${fmtGap(gapS)}점)
- 관계 거리(E): 현재 ${scores.E}점 → 이상 ${idealE}점 (격차 ${fmtGap(gapE)}점)
- 행동 추진력(F): 현재 ${scores.F}점 → 이상 ${idealF}점 (격차 ${fmtGap(gapF)}점)
- 핵심 충돌 축: ${coreAxis}축 (격차가 가장 큰 축, 이 축을 중심으로 분석할 것)

---

# 이 사람의 심리 구조 (분석 재료 — 문장을 그대로 옮기거나 요약하지 말 것)

${premiumBlock}

${axisBlock}

---

# 이 사람이 원하는 이상향 (Beyond)

${beyondBlock}

---

# 분석 작성 원칙

1. 수치(점수)는 리포트 본문에 **절대 직접 언급하지 마세요**. (괄호 포함 금지)
   대신 '매우 높다', '평균적이다', '낮은 편이다', '격차가 크다', '유사하다' 등 **정성적인 표현**으로 변환하여 서술하세요.
   금지(예시): 'S가 70점이고 이상향이 30점이므로 격차가 40점이다'
   허용(예시): '현재 감정 민감도가 매우 높은 편이나, 내면에서는 부침이 덜하기를 간절히 바라고 있어 큰 괴리가 있습니다.'

2. 위에서 제시된 심리 구조 텍스트의 단어/문장을 그대로 복사 붙여넣기 하지 마세요.
   같은 뜻이되 **더 자연스럽고 내담자에게 말하는 듯한 일상적인 단어**로 재해석하여 서술하세요.

3. Beyond 이상향은 '이것을 원한다'가 아니라,
   '지금 방식에서 무엇이 반복되고 있기에 저것을 원하게 됐는가'로 해석하세요.

4. 읽는 사람이 '이건 내 얘기다'라고 느껴야 합니다.
   추상 구조 나열 금지. 구체적인 장면, 감각, 대화 상황으로 서술하세요.

5. 모드: ${mode === 'love'
            ? '연애 편 — 파트너와의 상호작용, 친밀감, 갈등 패턴에 집중'
            : '일반 편 — 일상·직장·인간관계 전반의 내면 패턴에 집중'}
`.trim();

    // ── API 호출 (404 경로 불일치 fallback 포함) ────────────────────────────
    const endpointCandidates = [
        `${API_BASE}/analyze/inner-unified-report`,
        `${API_BASE}/analyze/inner-unified-report/`,
        `${API_BASE}/analysis/inner-unified-report`,
        `${API_BASE}/analysis/inner-unified-report/`,
        `${API_BASE}/inner-unified-report`,
        `${API_BASE}/inner-unified-report/`,
    ];

    let lastError = null;

    for (const endpoint of endpointCandidates) {
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ userPrompt, mode })
            });

            if (!res.ok) {
                const error = await res.json().catch(() => ({}));
                const msg = error.error || error.detail || `HTTP ${res.status}`;
                // 404/405는 다음 후보 경로로 재시도
                if (res.status === 404 || res.status === 405) {
                    lastError = new Error(msg);
                    continue;
                }
                throw new Error(msg);
            }
            return await res.json();
        } catch (err) {
            lastError = err;
            // 네트워크/경로 문제일 때만 다음 후보 재시도
            if (String(err?.message || '').includes('404') || String(err?.message || '').includes('Failed to fetch')) {
                continue;
            }
            console.error('API Error [getInnerUnifiedReport]:', err);
            throw err;
        }
    }

    const msg = String(lastError?.message || '');
    const isPathOrNetworkIssue = msg.includes('404') || msg.includes('405') || msg.includes('Failed to fetch');
    if (isPathOrNetworkIssue) {
        console.warn('API Error [getInnerUnifiedReport] fallback activated:', msg);
        return fallbackReport;
    }

    console.error('API Error [getInnerUnifiedReport][all endpoints failed]:', lastError);
    throw lastError || new Error('내면 분석 API 경로를 찾을 수 없습니다.');
};
